import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { DeleteOutlineRounded } from "@mui/icons-material";
import colors from "../../../../Constants/colors";
import { useDispatch, useSelector } from "react-redux";
import { getCombinations } from "../../../../features/chatbots/chatbotSlice";
import theme, { scrollbarStyles } from "../../../../Constants/theme";
import { applicationCardContainer } from "../ChatWindow/chatwindowStyles";
import {
  deleteButtonStyles,
  deleteIconStyles,
} from "../../../../Constants/buttonStyles";

const ApplicationsCard = ({
  name,
  setBot,
  app,
  setShowFileUploadArea,
  bot,
  handleDelete,
  setRetrievalCombinations,
  loadingDelete,
  setLoadingDelete,
  setGenerationCombinationId2,
  setRetrievalCombinationId2,
}) => {
  const organization = useSelector((state) => state.organization);

  const [displayConfirm, setDisplayConfirm] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [deleteID, setDeleteID] = useState("");
  const [applicationName, setapplicationName] = useState(""); // State to hold the input value
  const handleUse = async () => {
    const focus = document.getElementById(app._id["$oid"]);
    focus.scrollIntoView({ behavior: "smooth" });
    setShowFileUploadArea(true);
    setLoading(true);
    const response = await dispatch(
      getCombinations({ id: app._id["$oid"], items: 100, from: 0, data: {} })
    );
    setShowFileUploadArea(false);
    await setBot(app);
    await setRetrievalCombinations(response.payload.combinations);
    if (app?.generation_combinations?.length > 1) {
      setGenerationCombinationId2(app?.generation_combinations?.[1]?.["$oid"]);
    } else {
      setGenerationCombinationId2(app?.generation_combinations?.[0]?.["$oid"]);
    }
    setRetrievalCombinationId2(
      organization?.organization?.optimizedCombinations[app?._id["$oid"]]
    );

    setTimeout(() => {
      const topElement = document.getElementById("chatwindow");
      if (topElement) {
        topElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 300);
    setLoading(false);
  };

  const deleteBot = async () => {
    setDeleteID(app._id["$oid"]);
    setLoadingDelete(true);
    setDisplayConfirm(false); // Close the dialog after deleting
    await handleDelete(app._id["$oid"]);
  };

  function humanizeBotName(botName) {
    const match = botName?.match(
      /^(.+?)_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/
    );
    if (!match) return botName || "";

    const [, filename, year, month, day, hour, minute, second] = match;
    const date = new Date(+year, +month - 1, +day, +hour, +minute, +second);

    // e.g. "May 6, 2025 11:55:06"
    const formatted = date.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    return `${filename} (${formatted})`;
  }
  return (
    <Box id={app._id["$oid"]} sx={applicationCardContainer}>
      {/* Warning Dialog */}
      <Dialog
        open={displayConfirm}
        onClose={() => {
          setDisplayConfirm(false);
          setapplicationName(""); // Reset input on close
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            backgroundColor: "#2C2F3E",
            color: colors.extremeWhite,
          }}
        >
          Confirm Deletion
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: "#1A1A24",
            color: "#fff",
            padding: "30px",
          }}
        >
          <Typography sx={{ mt: 1 }}>
            To confirm deletion, type <strong>{name}</strong> below.
          </Typography>
          <Box
            component="input"
            placeholder="Enter application name"
            value={applicationName}
            onChange={(e) => setapplicationName(e.target.value)}
            sx={{
              backgroundColor: "#2C2F3E",
              border: "1px solid #444",
              borderRadius: "6px",
              color: "#fff",
              p: "8px 12px",
              mt: 2,
              width: "100%",
              fontSize: "14px",
              outline: "none",
              "&:focus": {
                borderColor: "#fca45b",
              },
            }}
          />
          <Typography sx={{ mt: 2, color: colors.warning }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            backgroundColor: "#1A1A24",
            padding: "8px",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setDisplayConfirm(false);
              setapplicationName("");
            }}
            sx={{
              color: "#fff",
              borderColor: "#fff",
              "&:hover": { borderColor: "#fca45b", color: "#fca45b" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={applicationName !== name || loadingDelete || loading}
            onClick={deleteBot}
            sx={{
              backgroundColor:
                applicationName === name ? "#c45902" : "rgba(255,255,255,0.2)",
              color: applicationName === name ? colors.bgLight : "#888",
              "&:hover": {
                backgroundColor:
                  applicationName === name
                    ? "#d32f2f"
                    : "rgba(255,255,255,0.2)",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"100%"}
      >
        {/* Heading */}
        <Tooltip title={humanizeBotName(name)}>
          <Typography fontSize="13.5px" fontWeight="600" noWrap>
            <b>Name:</b> {humanizeBotName(name)}
          </Typography>
        </Tooltip>

        {loadingDelete && deleteID === app._id["$oid"] ? (
          <CircularProgress />
        ) : (
          <IconButton
            onClick={() => setDisplayConfirm(true)}
            sx={{ ...deleteIconStyles }}
            disabled={loadingDelete || loading}
          >
            <DeleteOutlineRounded fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Typography fontSize="13.5px" fontWeight="600">
        Datasets:
      </Typography>
      <Box
        sx={{
          overflowY: "auto",
          maxHeight: "500px",
          height: "500px",
          flexDirection: "column",
          display: "flex",
          width: "100%",
          ...scrollbarStyles,
        }}
      >
        {(app?.datasets?.retrieval_1 || []).map((__DATA__, index) => (
          <Typography
            key={index}
            variant="p"
            sx={{
              color: theme.textColors.dark.primary,
              fontWeight: "thin",
              fontSize: "10px",
            }}
          >
            - {__DATA__?.file_name?.split("/")[2].slice(0, 20)}
          </Typography>
        ))}
      </Box>
      {(app?.initialised === "not started" ||
        app?.initialised === "unsuccessful" ||
        app?.initialised === "initialising") && (
        <Typography
          variant="p"
          sx={{
            color: "#ff9b9b",
            fontWeight: "thin",
            fontSize: "10px",
            p: 0.3,
            mt: "4px",
            border: "1px solid #ff9b9b",
            borderRadius: "4px",
          }}
        >
          Application status: {app?.initialised}
        </Typography>
      )}
      {/* Call-to-Action Button */}
      {loading ? (
        <CircularProgress />
      ) : (
        <Button
          variant="contained"
          sx={{
            backgroundColor: "var(--buttonBackground)",
            fontSize: "12px",
            color: "#fff",
            "&:hover": { backgroundColor: "#2980b9" },
            padding: "4px 3px",
            textTransform: "uppercase",
            marginTop: "20px",
          }}
          onClick={() => handleUse()}
          disabled={
            name === bot?.name ||
            loadingDelete ||
            loading ||
            app?.initialised === "not started" ||
            app?.initialised === "unsuccessful" ||
            app?.initialised === "initialising"
          }
        >
          {name === bot?.name ? "Selected Application" : "Use This Application"}
        </Button>
      )}
    </Box>
  );
};

export default ApplicationsCard;
